<script lang="ts" setup>
import { BaiduMap, BmCircle, BmLabel, BmMarker } from 'vue-baidu-map-3x'
import config from '~/common/config'
import type { CityRoundByMapAndMilesPageListVO } from '~/server/newhouse/interface'

defineOptions({
  name: 'RangeMap',
})

const props = defineProps<{
  /**
   * 小区名称
   */
  floor: string
  /**
   * 地图坐标纬度：地图坐标Y
   */
  baiduLatitude: number
  /**
   * 地图坐标经度：地图坐标X
   */
  baiduLongitude: number
  currentRound: number
  roundDataList: CityRoundByMapAndMilesPageListVO[]
  currentRoundId: number
}>()

const emits = defineEmits(['toggleCurrentRoundId'])

/**
 * 地图上 选中marker 的层级
 */
const selectedZIndex = ref(9999)

/**
 * 周边配套类别 图标
 */
const roundIconMap: { [key: number]: string } = {
  1: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon1.png',
  2: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon2.png',
  3: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon3.png',
  4: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon4.png',
  5: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon5.png',
}
/**
 * 周边配套地图上被选中的mark图标
 */
const activeRoundIconMap: { [key: number]: string } = {
  1: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon1_pressed.png',
  2: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon2_pressed.png',
  3: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon3_pressed.png',
  4: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon4_pressed.png',
  5: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon5_pressed.png',
}

/**
 * 小区 经纬度
 */
// const mapRef = ref(null)
const floorPos = computed(() => ({
  lng: props.baiduLongitude,
  lat: props.baiduLatitude,
}))

const center = computed(() => {
  const selectMark = props.roundDataList.find(item => item.roundId === props.currentRoundId)
  return selectMark
    ? {
        lng: selectMark.baiduLongitude,
        lat: selectMark.baiduLatitude,
      }
    : floorPos.value
})

const markerList = ref<CityRoundByMapAndMilesPageListVO[]>([])
watch(() => props.roundDataList, (val) => {
  markerList.value = Array.isArray(val) ? [...val] : val
}, {
  immediate: true,
})

watch(() => props.currentRoundId, (roundId) => {
  const selectedMarIndex = markerList.value.findIndex(item => item.roundId === roundId)
  if (selectedMarIndex > -1) {
    const selectMark = markerList.value.splice(selectedMarIndex, 1)
    nextTick(() => {
      markerList.value.push(...selectMark)
    })
  }
})
</script>

<template>
  <ClientOnly>
    <BaiduMap
      class="mapin"
      :ak="config.STATIC_MAP.key"
      v="3.0"
      :center="center"
      :zoom="14"
      :min-zoom="13"
      scroll-wheel-zoom
    >
      <!-- 圈圈 3km 范围 -->
      <BmCircle :center="floorPos" :radius="1000 * 3" stroke-color="#1DA838" :stroke-opacity="0.4" :stroke-weight="2" :fill-opacity="0.2" fill-color="rgba(82, 190, 84, 0.2)" />
      <BmCircle :center="floorPos" :radius="1000 * 2" stroke-color="#1DA838" :stroke-opacity="0.4" :stroke-weight="2" fill-color="rgba(82, 190, 84, 0)" />
      <BmCircle :center="floorPos" :radius="1000 * 1" stroke-color="#1DA838" :stroke-opacity="0.4" :stroke-weight="2" fill-color="rgba(82, 190, 84, 0)" />
      <!-- 小区位置 -->
      <!-- :icon="{ url: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/default_icon.png', size: { width: 32, height: 55 } }" -->

      <BmMarker :position="floorPos" :offset="{ width: 0, height: 20 }" :icon="{ url: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/default_icon.png', size: { width: 32, height: 56 } }" :z-index="currentRoundId === -1 ? selectedZIndex : 0">
        <!-- 三角样式 -->
        <BmLabel
          content=""
          :label-style="{
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '8px solid #20C063',
            marginLeft: '7px',
          }"
          :offset="{ height: 0 }"
        />
        <BmLabel
          :content="floor"
          :label-style="{
            color: '#fff',
            fontSize: '18px',
            textAlign: 'center',
            fontWeight: 'bold',
            backgroundColor: '#20C063',
            borderRadius: '5px',
            border: 'none',
            padding: '10px',
            transform: 'translate(-50%, -48%)',
            marginLeft: '14px',
          }"
          :offset="{ height: -20 }"
        />
      </BmMarker>
      <BmMarker
        v-for="mark in markerList" :key="mark.roundId"
        :position="{
          lat: mark.baiduLatitude,
          lng: mark.baiduLongitude,
        }"
        :icon="{
          url: mark.roundId === currentRoundId ? activeRoundIconMap[currentRound] : roundIconMap[currentRound],
          size: { width: 30, height: 54 },
          opts: {
            imageSize: { width: 30, height: 36.5 },
          },
        }
        "
        :z-index="currentRoundId === mark.roundId ? selectedZIndex : 0"
        @click="emits('toggleCurrentRoundId', mark.roundId)"
      >
        <!-- 三角样式 -->
        <BmLabel
          content=""
          :label-style="{
            // boxShadow: '0px 1px 1px #888',
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: mark.roundId === currentRoundId ? '8px solid #F0874E' : '8px solid #64A6FD',
            marginLeft: '5px',
          }"
          :offset="{ height: -10 }"
          @click="emits('toggleCurrentRoundId', mark.roundId)"
        />
        <BmLabel
          :content="mark.name"
          :label-style="{
            color: '#fff',
            fontSize: '14px',
            textAlign: 'center',
            fontWeight: 'bold',
            backgroundColor: mark.roundId === currentRoundId ? '#F0874E' : '#64A6FD',
            borderRadius: '5px',
            border: 'none',
            padding: '10px',
            // boxShadow: '0px 1px 1px #888',
            transform: 'translateY(-48%)',
            marginLeft: `${13 - (((mark.name?.length || 0) * 14 + 20) / 2)}px`,
          }"
          :offset="{ height: -30 }"
          @click="emits('toggleCurrentRoundId', mark.roundId)"
        />
      </BmMarker>
    </BaiduMap>
  </ClientOnly>
</template>

<style lang="scss" scoped>
    .mapin {
      width: 100%;
      height: 100%;
    }
</style>
